import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Chip,
  Card,
  CardContent,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import { hasWritePermission } from '../../utils/menuFilter';
import {
  Search,
  Person,
  SwapHoriz,
  Receipt,
  Home,
  AttachMoney,
  Business,
  CheckCircle,
  TransferWithinAStation,
} from '@mui/icons-material';

const TransferBooking = () => {
  const canWriteTransfer = hasWritePermission('plot-booking.transfer-booking');
  const [customerId, setCustomerId] = useState('');
  const [showBookingList, setShowBookingList] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [transferForm, setTransferForm] = useState({
    newCustomerId: '',
    newCustomerName: '',
    newCustomerMobile: '',
    newCustomerEmail: '',
    transferReason: ''
  });
  const [errors, setErrors] = useState({});
  const [searchLoading, setSearchLoading] = useState(false);
  const [transferLoading, setTransferLoading] = useState(false);

  // Sample booking data - this would come from API based on customer ID
  const [bookingData] = useState([
    {
      id: 1,
      bookingId: 'BK001',
      customerName: 'John Doe',
      bookingAmount: '₹5,00,000',
      siteName: 'Green Valley',
      sectorName: 'Sector A',
      blockName: 'Block 1',
      plotNo: 'P101',
      plotAmount: '₹15,00,000',
      siteRate: '₹3,500/sq ft',
      plotArea: '1200 sq ft',
      status: 'Booked',
    },
    {
      id: 2,
      bookingId: 'BK002',
      customerName: 'John Doe',
      bookingAmount: '₹7,50,000',
      siteName: 'Green Valley',
      sectorName: 'Sector B',
      blockName: 'Block 2',
      plotNo: 'P205',
      plotAmount: '₹18,00,000',
      siteRate: '₹4,000/sq ft',
      plotArea: '1500 sq ft',
      status: 'Booked',
    },
    {
      id: 3,
      bookingId: 'BK003',
      customerName: 'John Doe',
      bookingAmount: '₹3,00,000',
      siteName: 'Hill View',
      sectorName: 'Sector C',
      blockName: 'Block 1',
      plotNo: 'P087',
      plotAmount: '₹12,00,000',
      siteRate: '₹3,000/sq ft',
      plotArea: '1000 sq ft',
      status: 'Booked',
    },
  ]);

  const handleCustomerSearch = async () => {
    if (!customerId.trim()) {
      setErrors({ customerId: 'Customer ID is required' });
      return;
    }

    setSearchLoading(true);
    setErrors({});

    // Simulate API call
    setTimeout(() => {
      setShowBookingList(true);
      console.log('Searching bookings for customer ID:', customerId);
      setSearchLoading(false);
    }, 1500);
  };

  const handleTransferBooking = (booking) => {
    setSelectedBooking(booking);
    setTransferDialogOpen(true);
  };

  const handleCloseTransferDialog = () => {
    setTransferDialogOpen(false);
    setSelectedBooking(null);
    setTransferForm({
      newCustomerId: '',
      newCustomerName: '',
      newCustomerMobile: '',
      newCustomerEmail: '',
      transferReason: ''
    });
    setErrors({});
  };

  const handleTransferFormChange = (e) => {
    const { name, value } = e.target;
    setTransferForm({ ...transferForm, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateTransferForm = () => {
    const newErrors = {};

    if (!transferForm.newCustomerId.trim()) {
      newErrors.newCustomerId = 'New Customer ID is required';
    }

    if (!transferForm.newCustomerName.trim()) {
      newErrors.newCustomerName = 'New Customer Name is required';
    }

    if (!transferForm.newCustomerMobile.trim()) {
      newErrors.newCustomerMobile = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(transferForm.newCustomerMobile)) {
      newErrors.newCustomerMobile = 'Please enter a valid 10-digit mobile number';
    }

    if (!transferForm.newCustomerEmail.trim()) {
      newErrors.newCustomerEmail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(transferForm.newCustomerEmail)) {
      newErrors.newCustomerEmail = 'Please enter a valid email address';
    }

    if (!transferForm.transferReason.trim()) {
      newErrors.transferReason = 'Transfer reason is required';
    }

    return newErrors;
  };

  const handleSubmitTransfer = async () => {
    const validationErrors = validateTransferForm();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setTransferLoading(true);

    // Simulate API call
    setTimeout(() => {
      alert(`Booking ${selectedBooking.bookingId} transferred to ${transferForm.newCustomerName}`);
      console.log('Transfer booking:', {
        fromBooking: selectedBooking,
        toCustomer: transferForm
      });
      setTransferLoading(false);
      handleCloseTransferDialog();
    }, 2000);
  };

  const handleCustomerIdChange = (e) => {
    setCustomerId(e.target.value);
    if (errors.customerId) {
      setErrors({ ...errors, customerId: '' });
    }
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#fafafa', minHeight: 'auto' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
          Booking Transfer Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Transfer existing bookings from one customer to another
        </Typography>
      </Box>

      {/* Search Section */}
      <Card sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <Person sx={{ color: '#1976d2', fontSize: 28 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
              Search Customer Bookings
            </Typography>
          </Box>

          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="From Customer ID"
                value={customerId}
                onChange={handleCustomerIdChange}
                error={!!errors.customerId}
                helperText={errors.customerId}
                placeholder="Enter Customer ID (e.g., CUST001)"
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleCustomerSearch}
                disabled={searchLoading}
                startIcon={<Search />}
                sx={{
                  height: 56,
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '15px',
                  boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
                  borderRadius: 2
                }}
              >
                {searchLoading ? 'Searching...' : 'Search'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Booking Details Table */}
      {showBookingList && (
        <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <Receipt sx={{ color: '#1976d2', fontSize: 28 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
                Booking Details for Customer ID: {customerId}
              </Typography>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#000' }}>S.No</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#000' }}>Booking Info</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#000' }}>Customer</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#000' }}>Plot Details</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#000' }}>Financial Details</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#000' }}>Status</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: '14px', color: '#000' }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bookingData.map((booking, index) => (
                    <TableRow
                      key={booking.id}
                      sx={{
                        '&:hover': { backgroundColor: '#f9fafb' },
                        '& td': { py: 2 }
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600, color: '#000' }}>
                        {index + 1}
                      </TableCell>

                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#000' }}>
                            {booking.bookingId}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {booking.siteName}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#000' }}>
                            {booking.customerName}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#000' }}>
                            Plot {booking.plotNo}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {booking.sectorName}, {booking.blockName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {booking.plotArea}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#000' }}>
                            {booking.plotAmount}
                          </Typography>
                          <Typography variant="caption" color="success.main">
                            Paid: {booking.bookingAmount}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Rate: {booking.siteRate}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={booking.status}
                          color={booking.status === 'Booked' ? 'success' : 'default'}
                          size="small"
                          sx={{ fontWeight: 600, fontSize: '12px' }}
                        />
                      </TableCell>

                      <TableCell align="center">
                        {/* {canWriteTransfer && ( */}
                        <Tooltip title="Transfer Booking">
                          <IconButton
                            color="primary"
                            onClick={() => handleTransferBooking(booking)}
                            sx={{
                              '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.1)' }
                            }}
                          >
                            <TransferWithinAStation />
                          </IconButton>
                        </Tooltip>
                        {/* )} */}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {bookingData.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Alert severity="info">
                  No bookings found for this customer ID.
                </Alert>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Transfer Dialog */}
      <Dialog
        open={transferDialogOpen}
        onClose={handleCloseTransferDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{
          fontSize: '24px',
          fontWeight: 700,
          color: '#000',
          borderBottom: '1px solid #e0e0e0',
          pb: 2
        }}>
          <Box display="flex" alignItems="center" gap={2}>
            <TransferWithinAStation sx={{ color: '#1976d2', fontSize: 28 }} />
            Transfer Booking
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {selectedBooking && (
            <Box>
              {/* Current Booking Info */}
              <Card sx={{ p: 2, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>
                  Current Booking Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Booking ID</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedBooking.bookingId}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Current Customer</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedBooking.customerName}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Plot</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedBooking.plotNo}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Amount</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedBooking.plotAmount}</Typography>
                  </Grid>
                </Grid>
              </Card>

              <Divider sx={{ my: 3 }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <SwapHoriz sx={{ color: '#f57c00' }} />
                  <Typography variant="body2" color="text.secondary">Transfer To</Typography>
                </Box>
              </Divider>

              {/* Transfer Form */}
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="New Customer ID"
                    name="newCustomerId"
                    value={transferForm.newCustomerId}
                    onChange={handleTransferFormChange}
                    error={!!errors.newCustomerId}
                    helperText={errors.newCustomerId}
                    placeholder="Enter new customer ID"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="New Customer Name"
                    name="newCustomerName"
                    value={transferForm.newCustomerName}
                    onChange={handleTransferFormChange}
                    error={!!errors.newCustomerName}
                    helperText={errors.newCustomerName}
                    placeholder="Enter full name"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Mobile Number"
                    name="newCustomerMobile"
                    value={transferForm.newCustomerMobile}
                    onChange={handleTransferFormChange}
                    error={!!errors.newCustomerMobile}
                    helperText={errors.newCustomerMobile}
                    placeholder="10-digit mobile number"
                    InputProps={{
                      inputProps: { maxLength: 10 }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    name="newCustomerEmail"
                    type="email"
                    value={transferForm.newCustomerEmail}
                    onChange={handleTransferFormChange}
                    error={!!errors.newCustomerEmail}
                    helperText={errors.newCustomerEmail}
                    placeholder="Enter email address"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Transfer Reason"
                    name="transferReason"
                    value={transferForm.transferReason}
                    onChange={handleTransferFormChange}
                    error={!!errors.transferReason}
                    helperText={errors.transferReason}
                    placeholder="Please specify the reason for transfer"
                    multiline
                    rows={3}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button
            onClick={handleCloseTransferDialog}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            Cancel
          </Button>
          {/* {canWriteTransfer && ( */}
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmitTransfer}
            disabled={transferLoading}
            startIcon={<TransferWithinAStation />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)'
            }}
          >
            {transferLoading ? 'Transferring...' : 'Transfer Booking'}
          </Button>
          {/* )} */}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TransferBooking;